import { queryOptions, useMutation } from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';
import type { PaginatedResult, PaginationResult } from '@/services/types';
import { translateInactiveSireErrors } from '@/utils/sire';

import type {
  GetDonorScheduleResponse,
  SoScheduleEditResponse,
  UpdateSoScheduleRequest,
  UpsertDonorScheduleRequest,
  UpsertDonorScheduleResponse,
} from './types';

export type {
  GetDonorScheduleResponse,
  GetDonorSchedulesParams,
  SoScheduleEditResponse,
  SoScheduleLookupRef,
  UpdateSoScheduleRequest,
  UpsertDonorScheduleRequest,
  UpsertDonorScheduleResponse,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(token: AccessToken): HeadersInit {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchGet<T>(
  token: AccessToken,
  path: string,
  params?: URLSearchParams,
): Promise<T> {
  const url = params
    ? `${getBaseUrl()}/api/DonorSchedule/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/DonorSchedule/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchMutation<T>(
  token: AccessToken,
  path: string,
  method: 'POST' | 'PUT',
  body: unknown,
): Promise<T> {
  const url = `${getBaseUrl()}/api/DonorSchedule/${path}`;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(token),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

export async function fetchDonorSchedules(
  token: AccessToken,
  searchParams: URLSearchParams,
): Promise<PaginationResult<GetDonorScheduleResponse>> {
  return fetchGet<PaginationResult<GetDonorScheduleResponse>>(
    token,
    'schedules',
    searchParams,
  );
}

async function upsertDonorSchedule(
  token: AccessToken,
  request: UpsertDonorScheduleRequest,
): Promise<UpsertDonorScheduleResponse> {
  return fetchMutation<UpsertDonorScheduleResponse>(
    token,
    'upsert',
    'POST',
    request,
  );
}

export const donorSchedulesRestOptions = (
  token: AccessToken,
  params: URLSearchParams,
) =>
  queryOptions({
    queryKey: ['donor-scheduling', 'schedules', params.toString()],
    async queryFn() {
      return fetchDonorSchedules(token, params);
    },
    enabled: !!token,
  });

export function useUpsertDonorScheduleRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (request: UpsertDonorScheduleRequest) =>
      upsertDonorSchedule(token, request),
  });
}

export function useUpsertDonorScheduleV2RestMutation() {
  const token = useAccessToken();
  return useMutation({
    async mutationFn(
      request: UpsertDonorScheduleRequest,
    ): Promise<UpsertDonorScheduleResponse> {
      const response = await fetch(
        `${getBaseUrl()}/api/v2/donor-schedule/upsert`,
        {
          method: 'POST',
          credentials: 'include',
          headers: getHeaders(token),
          body: JSON.stringify(request),
        },
      );

      if (response.status === 400) {
        const body = await response.text();
        return {
          code: 400,
          success: false,
          message: translateInactiveSireErrors(body) ?? body,
        };
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return (await response.json()) as UpsertDonorScheduleResponse;
    },
  });
}

export type MassSetFieldRequest = {
  fields: string[];
  value: string;
  donorScheduleIds: string[];
};

export function useMassSetFieldRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (request: MassSetFieldRequest) =>
      fetchMutation<unknown>(token, 'mass-set-field', 'PUT', request),
  });
}

export function useMassSetFieldV2RestMutation() {
  const token = useAccessToken();
  return useMutation({
    async mutationFn(request: MassSetFieldRequest): Promise<void> {
      const response = await fetch(
        `${getBaseUrl()}/api/v2/donor-schedule/mass-set-field`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: getHeaders(token),
          body: JSON.stringify(request),
        },
      );

      if (!response.ok) {
        const body = await response.text();
        throw new Error(
          translateInactiveSireErrors(body) ??
            (body || `HTTP error! status: ${response.status}`),
        );
      }
    },
  });
}

/**
 * totalCount is only returned for the first block when the API reads from the search index,
 * and is capped server side (totalCountCapped). It's null otherwise.
 */
export type DonorSchedulePaginatedResult =
  PaginatedResult<GetDonorScheduleResponse> & {
    totalCount?: number | null;
    totalCountCapped?: boolean;
  };

export function donorSchedulesQueryOptions(
  token: AccessToken,
  params: URLSearchParams,
) {
  return queryOptions({
    queryKey: ['donor-scheduling', 'schedules', params.toString()],
    async queryFn() {
      const response = await fetch(
        `${getBaseUrl()}/api/v2/donor-schedule/schedules?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: getHeaders(token),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return (await response.json()) as DonorSchedulePaginatedResult;
    },
    enabled: !!token,
  });
}

export function useReactivateDonorSchedules() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: async (donorScheduleIds: string[]) => {
      const response = await fetch(
        `${getBaseUrl()}/api/v2/donor-schedule/reactivate`,
        {
          method: 'POST',
          credentials: 'include',
          headers: getHeaders(token),
          body: JSON.stringify(donorScheduleIds),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return (await response.json()) as string[];
    },
  });
}

export type CreateShipmentGroupResult = {
  code: number;
  success: boolean;
  message?: string | null;
  shipmentGroupId: string;
};

export function useCreateShipmentGroupRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: { name: string; estimations: number }) =>
      fetchMutation<CreateShipmentGroupResult>(
        token,
        'shipment-group',
        'POST',
        params,
      ),
  });
}

export type CreateShipmentGroupMasterRequest = {
  shipmentGroupId: string;
  name: string;
  contactId: string;
  billIncubator?: boolean;
  billShipping?: boolean;
  shippingMethod?: number;
  shipComment?: string | null;
  callTagComment?: string | null;
  siteId?: string | null;
  callTagContactId?: string | null;
  comment?: string | null;
};

export function useCreateShipmentGroupMasterRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: CreateShipmentGroupMasterRequest) =>
      fetchMutation<CreateShipmentGroupResult>(
        token,
        'shipment-group-master',
        'POST',
        params,
      ),
  });
}

export type SchedMasterWithAspirationDateDto = {
  aspirationDate?: string | null;
  leftSmallFollicles?: number | null;
  leftMediumFollicles?: number | null;
  leftLargeFollicles?: number | null;
  rightSmallFollicles?: number | null;
  rightMediumFollicles?: number | null;
  rightLargeFollicles?: number | null;
  gradeAbc?: number | null;
  gradeD?: number | null;
  gradeE?: number | null;
  dead?: number | null;
  numberInMat?: number | null;
  ivcNumber?: number | null;
  cleaved?: number | null;
  quality?: number | null;
  low?: number | null;
  totalEmbryos?: number | null;
};

async function fetchPreviousSchedulesV3(
  token: AccessToken,
  {
    donorScheduleId,
    donorId,
    ownerId,
  }: {
    donorScheduleId: string;
    donorId: string;
    ownerId: string;
  },
): Promise<SchedMasterWithAspirationDateDto[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorScheduleId', donorScheduleId);
  searchParams.set('donorId', donorId);
  searchParams.set('ownerId', ownerId);

  const url = `${getBaseUrl()}/api/v3/donorschedule/previous-schedules?${searchParams.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as SchedMasterWithAspirationDateDto[];
}

export const previousSchedulesV3Options = (
  token: AccessToken,
  {
    donorScheduleId,
    donorId,
    ownerId,
  }: {
    donorScheduleId: string;
    donorId: string;
    ownerId: string;
  },
) =>
  queryOptions({
    queryKey: [
      'previousSchedules',
      'v3',
      'list',
      { donorScheduleId, donorId, ownerId },
    ],
    async queryFn() {
      return fetchPreviousSchedulesV3(token, {
        donorScheduleId,
        donorId,
        ownerId,
      });
    },
    retry: false,
    staleTime: Infinity,
    enabled: !!token,
  });

export const soScheduleEditOptions = (
  token: AccessToken,
  soScheduleId: string | null | undefined,
) =>
  queryOptions({
    queryKey: ['donor-scheduling', 'so-schedule', soScheduleId],
    async queryFn() {
      return fetchGet<SoScheduleEditResponse>(
        token,
        `so-schedule/${soScheduleId}`,
      );
    },
    enabled: !!token && !!soScheduleId,
  });

/** Surfaces the API's validation/lock message (400/409) instead of only the status code. */
async function updateSoSchedule(
  token: AccessToken,
  soScheduleId: string,
  request: UpdateSoScheduleRequest,
): Promise<SoScheduleEditResponse> {
  const response = await fetch(
    `${getBaseUrl()}/api/DonorSchedule/so-schedule/${soScheduleId}`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: getHeaders(token),
      body: JSON.stringify(request),
    },
  );

  if (!response.ok) {
    const message =
      response.status === 400 || response.status === 409
        ? await response.text()
        : '';
    throw new Error(
      message.replace(/^"|"$/g, '') || `HTTP error! status: ${response.status}`,
    );
  }

  return (await response.json()) as SoScheduleEditResponse;
}

export function useUpdateSoScheduleRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: ({
      soScheduleId,
      request,
    }: {
      soScheduleId: string;
      request: UpdateSoScheduleRequest;
    }) => updateSoSchedule(token, soScheduleId, request),
  });
}
